import React, { useState, useRef, useCallback } from 'react';
import Layout from '../components/Layout';
import {
  Globe, Rss, FileSpreadsheet, FileText, Twitter, BookOpen,
  Webhook, Code2, Send, ChevronRight, X, Save, Cpu,
  Plus, Trash2, Play, Info, GitBranch, Layers
} from 'lucide-react';

// ─── Types ────────────────────────────────────────────────────────────────────
type NodeKind = 'trigger' | 'action' | 'destination';
type NodeType =
  | 'url' | 'rss' | 'csv'                             // triggers
  | 'longform' | 'xthread' | 'structuredjson'          // actions  (DITA replaced by Structured JSON)
  | 'strapi' | 'sanity' | 'contentful' | 'webhook' | 'rest';  // destinations (headless CMS added)

interface WorkflowNode {
  id: string;
  kind: NodeKind;
  type: NodeType;
  label: string;
  x: number;
  y: number;
  systemPromptOverride?: string;
  config?: Record<string, string>;
}

interface Edge {
  from: string;
  to: string;
}

// ─── Node meta ────────────────────────────────────────────────────────────────
const NODE_META: Record<NodeType, { icon: React.FC<{ className?: string }>; color: string; desc: string }> = {
  url:            { icon: Globe,          color: 'from-cyan-500 to-blue-500',      desc: 'Single URL scrape trigger' },
  rss:            { icon: Rss,            color: 'from-orange-500 to-amber-500',   desc: 'RSS Feed polling trigger' },
  csv:            { icon: FileSpreadsheet,color: 'from-emerald-500 to-teal-500',  desc: 'CSV bulk upload trigger' },
  longform:       { icon: FileText,       color: 'from-violet-500 to-purple-500',  desc: 'Long-form article generator' },
  xthread:        { icon: Twitter,        color: 'from-sky-500 to-blue-500',       desc: 'X / Twitter thread generator' },
  structuredjson: { icon: BookOpen,       color: 'from-indigo-500 to-cyan-500',    desc: 'Structured JSON document (Headless CMS ready)' },
  strapi:         { icon: Code2,          color: 'from-violet-600 to-indigo-500',  desc: 'Strapi CMS — REST API delivery' },
  sanity:         { icon: Send,           color: 'from-rose-500 to-orange-500',    desc: 'Sanity.io — Content Lake API' },
  contentful:     { icon: Globe,          color: 'from-blue-500 to-cyan-500',      desc: 'Contentful — Content Management API' },
  webhook:        { icon: Webhook,        color: 'from-pink-500 to-rose-500',      desc: 'Generic webhook POST' },
  rest:           { icon: Code2,          color: 'from-slate-500 to-zinc-500',     desc: 'Custom REST endpoint POST' },
};

const TRIGGER_TYPES: NodeType[]     = ['url', 'rss', 'csv'];
const ACTION_TYPES: NodeType[]      = ['longform', 'xthread', 'structuredjson'];
const DESTINATION_TYPES: NodeType[] = ['strapi', 'sanity', 'contentful', 'webhook', 'rest'];

// ─── Structured JSON schema template ─────────────────────────────────────────
export const STRUCTURED_JSON_SCHEMA = {
  entry_meta: { title: '', slug: '', content_type: 'article' },
  components: [
    { type: 'hero_section',  fields: { heading: '', lead_text: '' } },
    { type: 'rich_text',     fields: { body_markdown: '' } },
    { type: 'code_block',    fields: { language: 'typescript', code_snippet: '' } },
  ],
  seo_payload: { meta_title: '', meta_description: '', keywords: [] as string[] },
};

const STRUCTURED_JSON_PROMPT = `OUTPUT FORMAT: Respond ONLY with a valid JSON object — no markdown, no explanation.
Schema:
{
  "entry_meta": { "title": "string", "slug": "url-safe-slug", "content_type": "article|documentation|marketing" },
  "components": [
    { "type": "hero_section",  "fields": { "heading": "string", "lead_text": "string" } },
    { "type": "rich_text",     "fields": { "body_markdown": "string (markdown)" } },
    { "type": "code_block",    "fields": { "language": "string", "code_snippet": "string" } }
  ],
  "seo_payload": { "meta_title": "string", "meta_description": "≤155 chars", "keywords": ["string"] }
}
Generate a thorough, SEO-optimised structured document for the given topic. Minimum 3 components.`;

const DEFAULT_PROMPTS: Partial<Record<NodeType, string>> = {
  longform:       'OUTPUT FORMAT: Structured JSON only — no markdown, no preamble.\n{"entry_meta":{"title":"","slug":"","content_type":"article"},"components":[{"type":"hero_section","fields":{"heading":"","lead_text":""}},{"type":"rich_text","fields":{"body_markdown":""}}],"seo_payload":{"meta_title":"","meta_description":"","keywords":[]}}\n\nWrite a 1,200-word thought-leadership blog. Use H2/H3 structure. Open with a bold hook. Close with strong CTA. Avoid filler.',
  xthread:        'Create an 8-tweet X thread. Start with scroll-stopping hook. Each tweet < 280 chars. End with call-to-follow. No hashtag spam.',
  structuredjson: STRUCTURED_JSON_PROMPT,
};

// ─── SVG arrow between nodes ──────────────────────────────────────────────────
const Arrow: React.FC<{ x1: number; y1: number; x2: number; y2: number }> = ({ x1, y1, x2, y2 }) => {
  const mx = (x1 + x2) / 2;
  const d  = `M ${x1} ${y1} C ${mx} ${y1}, ${mx} ${y2}, ${x2} ${y2}`;
  return (
    <g>
      <path d={d} fill="none" stroke="rgba(100,70,200,0.28)" strokeWidth="1.5" strokeDasharray="5 3" />
      <path d={d} fill="none" stroke="rgba(80,50,180,0.10)" strokeWidth="5" />
      <polygon
        points={`${x2},${y2} ${x2 - 8},${y2 - 4} ${x2 - 8},${y2 + 4}`}
        fill="rgba(100,70,200,0.45)"
      />
    </g>
  );
};

// ─── Single canvas node card ──────────────────────────────────────────────────
const NodeCard: React.FC<{
  node: WorkflowNode;
  selected: boolean;
  onClick: () => void;
  onDelete: () => void;
}> = ({ node, selected, onClick, onDelete }) => {
  const meta = NODE_META[node.type];
  const Icon = meta.icon;
  const kindColors: Record<NodeKind, string> = {
    trigger:     'border-cyan-500/25',
    action:      'border-violet-500/25',
    destination: 'border-emerald-500/25',
  };
  return (
    <div
      onClick={onClick}
      style={{ position: 'absolute', left: node.x, top: node.y, width: 160 }}
      className={`rounded-xl border bg-[#12121e]/90 backdrop-blur-sm shadow-lg cursor-pointer select-none transition-all duration-150
        ${kindColors[node.kind]}
        ${selected ? 'ring-1 ring-violet-400/50 shadow-[0_0_14px_rgba(139,92,246,0.2)]' : 'hover:border-violet-500/25'}
      `}
    >
      {/* Header */}
      <div className={`rounded-t-xl bg-gradient-to-r ${meta.color} bg-opacity-10 px-3 py-2 flex items-center gap-2`}>
        <div className={`w-6 h-6 rounded-md bg-gradient-to-br ${meta.color} flex items-center justify-center flex-shrink-0`}>
          <Icon className="w-3.5 h-3.5 text-white" />
        </div>
        <span className="text-xs font-semibold text-white truncate flex-1">{node.label}</span>
        <button
          onClick={e => { e.stopPropagation(); onDelete(); }}
          className="p-0.5 hover:bg-white/10 rounded text-white/40 hover:text-white/80 transition-colors"
        >
          <X className="w-3 h-3" />
        </button>
      </div>
      {/* Body */}
      <div className="px-3 py-2">
        <p className="text-[10px] text-slate-400/80 leading-relaxed">{meta.desc}</p>
        {node.kind === 'action' && (
          <div className="mt-1.5 flex items-center gap-1 text-[10px] text-violet-400">
            <Cpu className="w-3 h-3" />
            <span>{node.systemPromptOverride ? 'Custom prompt' : 'Default prompt'}</span>
          </div>
        )}
      </div>
      {/* Port dots */}
      <div className="absolute left-1/2 -translate-x-1/2 -top-2 w-3 h-3 rounded-full bg-slate-700 border-2 border-slate-600" />
      <div className="absolute left-1/2 -translate-x-1/2 -bottom-2 w-3 h-3 rounded-full bg-slate-700 border-2 border-slate-600" />
    </div>
  );
};

// ─── Drawer for editing action node prompts ───────────────────────────────────
const NodeDrawer: React.FC<{
  node: WorkflowNode;
  onClose: () => void;
  onSave: (n: WorkflowNode) => void;
}> = ({ node, onClose, onSave }) => {
  const [prompt, setPrompt]       = useState(node.systemPromptOverride ?? DEFAULT_PROMPTS[node.type] ?? '');
  const [cfg, setCfg]             = useState<Record<string, string>>(node.config ?? {});
  const [drawerTab, setDrawerTab] = useState<'config' | 'json'>('config');
  const meta = NODE_META[node.type];
  const Icon = meta.icon;

  // Live JSON preview for action nodes — shows API payload structure
  const jsonPreview = JSON.stringify({
    ...STRUCTURED_JSON_SCHEMA,
    entry_meta: {
      title: node.label,
      slug: node.label.toLowerCase().replace(/\s+/g, '-'),
      content_type: node.type === 'xthread' ? 'marketing' : 'article',
    },
    _node_meta: { node_type: node.type, node_id: node.id, prompt_override: prompt.slice(0, 100) + (prompt.length > 100 ? '…' : '') },
  }, null, 2);

  return (
    <div className="fixed inset-0 z-50 flex items-end md:items-center justify-end p-0 md:p-6">
      {/* backdrop */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full md:w-[480px] h-[90vh] md:h-auto md:max-h-[85vh] bg-[#0d0f1a] border border-slate-700/60 rounded-t-2xl md:rounded-2xl overflow-hidden flex flex-col shadow-2xl">
        {/* Drawer header */}
        <div className={`px-5 py-4 bg-gradient-to-r ${meta.color} bg-opacity-10 border-b border-slate-700/60 flex items-center gap-3`}>
          <div className={`w-8 h-8 rounded-xl bg-gradient-to-br ${meta.color} flex items-center justify-center`}>
            <Icon className="w-4 h-4 text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-white text-sm">{node.label}</p>
            <p className="text-[11px] text-slate-400 capitalize">{node.kind} · {node.type}</p>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-white/10 rounded-lg text-slate-400 hover:text-white transition-colors">
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Drawer sub-tabs (only for action nodes) */}
        {node.kind === 'action' && (
          <div className="flex border-b border-slate-700/60">
            {(['config', 'json'] as const).map(t => (
              <button
                key={t}
                onClick={() => setDrawerTab(t)}
                className={`flex-1 py-2.5 text-xs font-semibold transition-colors ${
                  drawerTab === t
                    ? 'text-violet-400 border-b-2 border-violet-500'
                    : 'text-slate-500 hover:text-slate-300'
                }`}
              >
                {t === 'config' ? '⚙ Config & Prompt' : '{ } Raw API JSON'}
              </button>
            ))}
          </div>
        )}

        <div className="flex-1 overflow-y-auto p-5 space-y-5">

          {/* JSON raw view — action nodes only */}
          {node.kind === 'action' && drawerTab === 'json' ? (
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <Code2 className="w-4 h-4 text-violet-400" />
                <p className="text-sm font-semibold text-white">Raw API JSON Structure</p>
              </div>
              <p className="text-[11px] text-slate-500">
                This is the Structured JSON Document payload this node will push to a Headless CMS API.
                Modify field values via the Config tab; the schema stays fixed.
              </p>
              <pre className="text-[11px] text-emerald-300 font-mono whitespace-pre-wrap leading-relaxed bg-slate-900/80 rounded-xl p-4 border border-slate-700/40 overflow-x-auto">
                {jsonPreview}
              </pre>
              <div className="flex items-start gap-2 p-3 bg-indigo-500/10 border border-indigo-500/20 rounded-xl">
                <Info className="w-3.5 h-3.5 text-indigo-400 mt-0.5 flex-shrink-0" />
                <p className="text-[11px] text-indigo-300">
                  POST this payload to your CMS endpoint (Strapi / Sanity / Contentful) configured in the Destination node.
                  The <code className="bg-indigo-500/20 px-1 rounded">components</code> array maps directly to CMS content blocks.
                </p>
              </div>
            </div>
          ) : (
            <>
          {/* Config fields per type */}
          {node.type === 'url' && (
            <div>
              <label className="block text-xs font-semibold text-slate-400 mb-1.5">Source URL</label>
              <input
                className="w-full bg-slate-800/60 border border-slate-700/60 rounded-xl px-3 py-2 text-sm text-white placeholder:text-slate-600 focus:border-violet-500/50 focus:outline-none"
                placeholder="https://example.com/article"
                value={cfg.url ?? ''}
                onChange={e => setCfg(c => ({ ...c, url: e.target.value }))}
              />
            </div>
          )}
          {node.type === 'rss' && (
            <div>
              <label className="block text-xs font-semibold text-slate-400 mb-1.5">RSS Feed URL</label>
              <input className="w-full bg-slate-800/60 border border-slate-700/60 rounded-xl px-3 py-2 text-sm text-white placeholder:text-slate-600 focus:border-violet-500/50 focus:outline-none" placeholder="https://feeds.example.com/rss" value={cfg.rss ?? ''} onChange={e => setCfg(c => ({ ...c, rss: e.target.value }))} />
              <label className="block text-xs font-semibold text-slate-400 mb-1.5 mt-3">Poll interval (minutes)</label>
              <input type="number" className="w-full bg-slate-800/60 border border-slate-700/60 rounded-xl px-3 py-2 text-sm text-white focus:border-violet-500/50 focus:outline-none" value={cfg.interval ?? '60'} onChange={e => setCfg(c => ({ ...c, interval: e.target.value }))} />
            </div>
          )}
          {/* Headless CMS destination configs */}
          {node.type === 'strapi' && (
            <div className="space-y-3">
              <p className="text-[11px] text-violet-300 bg-violet-500/10 border border-violet-500/20 rounded-lg p-2">Strapi REST API — POSTs Structured JSON to your Strapi collection</p>
              <div><label className="block text-xs font-semibold text-slate-400 mb-1.5">API Base URL</label>
                <input className="w-full bg-slate-800/60 border border-slate-700/60 rounded-xl px-3 py-2 text-sm text-white placeholder:text-slate-600 focus:border-violet-500/50 focus:outline-none" placeholder="https://your-strapi.com/api" value={cfg.baseUrl ?? ''} onChange={e => setCfg(c => ({ ...c, baseUrl: e.target.value }))} /></div>
              <div><label className="block text-xs font-semibold text-slate-400 mb-1.5">Bearer Content Token</label>
                <input type="password" className="w-full bg-slate-800/60 border border-slate-700/60 rounded-xl px-3 py-2 text-sm text-white placeholder:text-slate-600 focus:border-violet-500/50 focus:outline-none" value={cfg.token ?? ''} onChange={e => setCfg(c => ({ ...c, token: e.target.value }))} /></div>
              <div><label className="block text-xs font-semibold text-slate-400 mb-1.5">Collection Slug</label>
                <input className="w-full bg-slate-800/60 border border-slate-700/60 rounded-xl px-3 py-2 text-sm text-white placeholder:text-slate-600 focus:border-violet-500/50 focus:outline-none" placeholder="articles" value={cfg.collection ?? ''} onChange={e => setCfg(c => ({ ...c, collection: e.target.value }))} /></div>
            </div>
          )}
          {node.type === 'sanity' && (
            <div className="space-y-3">
              <p className="text-[11px] text-rose-300 bg-rose-500/10 border border-rose-500/20 rounded-lg p-2">Sanity.io — Mutates content via GROQ-powered Content Lake API</p>
              <div><label className="block text-xs font-semibold text-slate-400 mb-1.5">Project ID</label>
                <input className="w-full bg-slate-800/60 border border-slate-700/60 rounded-xl px-3 py-2 text-sm text-white placeholder:text-slate-600 focus:border-violet-500/50 focus:outline-none" placeholder="abc12def" value={cfg.projectId ?? ''} onChange={e => setCfg(c => ({ ...c, projectId: e.target.value }))} /></div>
              <div><label className="block text-xs font-semibold text-slate-400 mb-1.5">Dataset</label>
                <input className="w-full bg-slate-800/60 border border-slate-700/60 rounded-xl px-3 py-2 text-sm text-white placeholder:text-slate-600 focus:border-violet-500/50 focus:outline-none" placeholder="production" value={cfg.dataset ?? 'production'} onChange={e => setCfg(c => ({ ...c, dataset: e.target.value }))} /></div>
              <div><label className="block text-xs font-semibold text-slate-400 mb-1.5">API Token</label>
                <input type="password" className="w-full bg-slate-800/60 border border-slate-700/60 rounded-xl px-3 py-2 text-sm text-white placeholder:text-slate-600 focus:border-violet-500/50 focus:outline-none" value={cfg.token ?? ''} onChange={e => setCfg(c => ({ ...c, token: e.target.value }))} /></div>
              <div><label className="block text-xs font-semibold text-slate-400 mb-1.5">Document Type</label>
                <input className="w-full bg-slate-800/60 border border-slate-700/60 rounded-xl px-3 py-2 text-sm text-white placeholder:text-slate-600 focus:border-violet-500/50 focus:outline-none" placeholder="article" value={cfg.docType ?? ''} onChange={e => setCfg(c => ({ ...c, docType: e.target.value }))} /></div>
            </div>
          )}
          {node.type === 'contentful' && (
            <div className="space-y-3">
              <p className="text-[11px] text-cyan-300 bg-cyan-500/10 border border-cyan-500/20 rounded-lg p-2">Contentful CMA — POSTs structured entries to your space</p>
              <div><label className="block text-xs font-semibold text-slate-400 mb-1.5">Space ID</label>
                <input className="w-full bg-slate-800/60 border border-slate-700/60 rounded-xl px-3 py-2 text-sm text-white placeholder:text-slate-600 focus:border-violet-500/50 focus:outline-none" value={cfg.spaceId ?? ''} onChange={e => setCfg(c => ({ ...c, spaceId: e.target.value }))} /></div>
              <div><label className="block text-xs font-semibold text-slate-400 mb-1.5">Environment</label>
                <input className="w-full bg-slate-800/60 border border-slate-700/60 rounded-xl px-3 py-2 text-sm text-white placeholder:text-slate-600 focus:border-violet-500/50 focus:outline-none" placeholder="master" value={cfg.env ?? 'master'} onChange={e => setCfg(c => ({ ...c, env: e.target.value }))} /></div>
              <div><label className="block text-xs font-semibold text-slate-400 mb-1.5">Content Management API Token</label>
                <input type="password" className="w-full bg-slate-800/60 border border-slate-700/60 rounded-xl px-3 py-2 text-sm text-white placeholder:text-slate-600 focus:border-violet-500/50 focus:outline-none" value={cfg.token ?? ''} onChange={e => setCfg(c => ({ ...c, token: e.target.value }))} /></div>
              <div><label className="block text-xs font-semibold text-slate-400 mb-1.5">Content Type ID</label>
                <input className="w-full bg-slate-800/60 border border-slate-700/60 rounded-xl px-3 py-2 text-sm text-white placeholder:text-slate-600 focus:border-violet-500/50 focus:outline-none" placeholder="article" value={cfg.contentTypeId ?? ''} onChange={e => setCfg(c => ({ ...c, contentTypeId: e.target.value }))} /></div>
            </div>
          )}
          {node.type === 'webhook' && (
            <div>
              <label className="block text-xs font-semibold text-slate-400 mb-1.5">Webhook URL</label>
              <input className="w-full bg-slate-800/60 border border-slate-700/60 rounded-xl px-3 py-2 text-sm text-white placeholder:text-slate-600 focus:border-violet-500/50 focus:outline-none" placeholder="https://hooks.zapier.com/..." value={cfg.webhookUrl ?? ''} onChange={e => setCfg(c => ({ ...c, webhookUrl: e.target.value }))} />
            </div>
          )}
          {node.type === 'rest' && (
            <div className="space-y-3">
              <div><label className="block text-xs font-semibold text-slate-400 mb-1.5">Endpoint URL</label>
                <input className="w-full bg-slate-800/60 border border-slate-700/60 rounded-xl px-3 py-2 text-sm text-white placeholder:text-slate-600 focus:border-violet-500/50 focus:outline-none" placeholder="https://api.myapp.com/content" value={cfg.restUrl ?? ''} onChange={e => setCfg(c => ({ ...c, restUrl: e.target.value }))} /></div>
              <div><label className="block text-xs font-semibold text-slate-400 mb-1.5">Bearer Token</label>
                <input type="password" className="w-full bg-slate-800/60 border border-slate-700/60 rounded-xl px-3 py-2 text-sm text-white placeholder:text-slate-600 focus:border-violet-500/50 focus:outline-none" value={cfg.restToken ?? ''} onChange={e => setCfg(c => ({ ...c, restToken: e.target.value }))} /></div>
            </div>
          )}

          {/* System prompt override — only for action nodes */}
          {node.kind === 'action' && (
            <div>
              <div className="flex items-center gap-2 mb-1.5">
                <label className="text-xs font-semibold text-slate-400">System Prompt Override</label>
                <span className="text-[10px] text-violet-400 bg-violet-500/10 border border-violet-500/20 rounded-full px-2 py-0.5">AI Instruction</span>
              </div>
              <p className="text-[11px] text-slate-600 mb-2">Overrides the global prompt for this node only. Adjacent nodes are unaffected.</p>
              <textarea
                className="w-full h-44 bg-slate-800/60 border border-slate-700/60 rounded-xl px-3 py-2.5 text-sm text-white placeholder:text-slate-600 focus:border-violet-500/50 focus:outline-none resize-none leading-relaxed font-mono"
                placeholder={DEFAULT_PROMPTS[node.type] ?? 'Enter custom instructions for this AI action…'}
                value={prompt}
                onChange={e => setPrompt(e.target.value)}
              />
              <div className="flex justify-end mt-1">
                <button onClick={() => setPrompt(DEFAULT_PROMPTS[node.type] ?? '')} className="text-[11px] text-slate-500 hover:text-violet-400 transition-colors">
                  ↺ Reset to default
                </button>
              </div>
            </div>
          )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-slate-700/60 flex gap-3">
          <button onClick={onClose} className="flex-1 px-4 py-2 bg-slate-800 hover:bg-slate-700 rounded-xl text-sm text-slate-400 transition-colors">
            Cancel
          </button>
          <button
            onClick={() => { onSave({ ...node, systemPromptOverride: prompt, config: cfg }); onClose(); }}
            className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-violet-600 hover:bg-violet-500 rounded-xl text-sm text-white font-semibold transition-colors"
          >
            <Save className="w-4 h-4" /> Save Node
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Node palette button ──────────────────────────────────────────────────────
const PaletteBtn: React.FC<{ type: NodeType; onAdd: (t: NodeType) => void }> = ({ type, onAdd }) => {
  const meta = NODE_META[type];
  const Icon = meta.icon;
  return (
    <button
      onClick={() => onAdd(type)}
      className="flex items-center gap-2 w-full px-3 py-2 rounded-xl bg-slate-800/60 hover:bg-slate-700/60 border border-slate-700/40 hover:border-violet-500/30 transition-all text-left group"
    >
      <div className={`w-7 h-7 rounded-lg bg-gradient-to-br ${meta.color} flex items-center justify-center flex-shrink-0`}>
        <Icon className="w-3.5 h-3.5 text-white" />
      </div>
      <div className="min-w-0">
        <p className="text-xs font-semibold text-white capitalize truncate">{type}</p>
        <p className="text-[10px] text-slate-500 truncate">{meta.desc}</p>
      </div>
      <Plus className="w-3.5 h-3.5 text-slate-600 group-hover:text-violet-400 ml-auto flex-shrink-0 transition-colors" />
    </button>
  );
};

// ─── Main page ────────────────────────────────────────────────────────────────
let nodeSeq = 0;
const mkId = () => `node_${++nodeSeq}_${Date.now()}`;

const INITIAL_NODES: WorkflowNode[] = [
  { id: mkId(), kind: 'trigger',     type: 'url',            label: 'Single URL',          x: 60,  y: 80  },
  { id: mkId(), kind: 'action',      type: 'longform',       label: 'Long-form Blog',      x: 260, y: 80  },
  { id: mkId(), kind: 'destination', type: 'strapi',         label: 'Strapi CMS',          x: 460, y: 80  },
  { id: mkId(), kind: 'trigger',     type: 'rss',            label: 'RSS Feed',            x: 60,  y: 240 },
  { id: mkId(), kind: 'action',      type: 'structuredjson', label: 'Structured JSON Doc', x: 260, y: 240 },
  { id: mkId(), kind: 'destination', type: 'contentful',     label: 'Contentful API',      x: 460, y: 240 },
];

const INITIAL_EDGES: Edge[] = [
  { from: INITIAL_NODES[0].id, to: INITIAL_NODES[1].id },
  { from: INITIAL_NODES[1].id, to: INITIAL_NODES[2].id },
  { from: INITIAL_NODES[3].id, to: INITIAL_NODES[4].id },
  { from: INITIAL_NODES[4].id, to: INITIAL_NODES[5].id },
];

const CanvasDesigner: React.FC = () => {
  const [nodes, setNodes]         = useState<WorkflowNode[]>(INITIAL_NODES);
  const [edges, setEdges]         = useState<Edge[]>(INITIAL_EDGES);
  const [selected, setSelected]   = useState<string | null>(null);
  const [drawerNode, setDrawer]   = useState<WorkflowNode | null>(null);
  const [connecting, setConnecting] = useState<string | null>(null);
  const [saved, setSaved]         = useState(false);
  const canvasRef                 = useRef<HTMLDivElement>(null);

  // drag state
  const dragging = useRef<{ id: string; ox: number; oy: number } | null>(null);

  const handleMouseDown = (e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    const node = nodes.find(n => n.id === id)!;
    dragging.current = { id, ox: e.clientX - node.x, oy: e.clientY - node.y };
  };

  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    if (!dragging.current) return;
    const { id, ox, oy } = dragging.current;
    setNodes(ns => ns.map(n => n.id === id ? { ...n, x: Math.max(0, e.clientX - ox), y: Math.max(0, e.clientY - oy) } : n));
  }, []);

  const handleMouseUp = useCallback(() => { dragging.current = null; }, []);

  const addNode = (type: NodeType) => {
    const kind: NodeKind = TRIGGER_TYPES.includes(type) ? 'trigger' : ACTION_TYPES.includes(type) ? 'action' : 'destination';
    const label = NODE_META[type].desc.split(' ').slice(0, 2).join(' ');
    setNodes(ns => [...ns, { id: mkId(), kind, type, label, x: 80 + Math.random() * 200, y: 80 + Math.random() * 200 }]);
  };

  const deleteNode = (id: string) => {
    setNodes(ns => ns.filter(n => n.id !== id));
    setEdges(es => es.filter(e => e.from !== id && e.to !== id));
    if (selected === id) setSelected(null);
  };

  const saveNode = (updated: WorkflowNode) => {
    setNodes(ns => ns.map(n => n.id === updated.id ? updated : n));
  };

  const handleConnect = (id: string) => {
    if (!connecting) { setConnecting(id); return; }
    if (connecting !== id) {
      setEdges(es => [...es, { from: connecting, to: id }]);
    }
    setConnecting(null);
  };

  const handleSave = () => {
    localStorage.setItem('uxcf_canvas_nodes', JSON.stringify(nodes));
    localStorage.setItem('uxcf_canvas_edges', JSON.stringify(edges));
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  // compute SVG arrows
  const arrows = edges.map(e => {
    const from = nodes.find(n => n.id === e.from);
    const to   = nodes.find(n => n.id === e.to);
    if (!from || !to) return null;
    return { key: `${e.from}-${e.to}`, x1: from.x + 80, y1: from.y + 60, x2: to.x + 80, y2: to.y };
  }).filter(Boolean);

  const selectedNode = nodes.find(n => n.id === selected);

  return (
    <Layout>
      <div className="h-full flex flex-col overflow-hidden">

        {/* Top bar */}
        <div className="flex-shrink-0 px-6 py-3 border-b border-white/[0.05] bg-[#0f0f16]/80 backdrop-blur-sm flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-violet-500 to-cyan-500 flex items-center justify-center">
              <GitBranch className="w-4 h-4 text-white" />
            </div>
            <div>
              <h1 className="font-bold text-white text-sm">Canvas Designer</h1>
              <p className="text-[11px] text-slate-500">Node-based workflow builder — drag, connect, configure</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {connecting && (
              <span className="text-xs text-amber-400 bg-amber-400/10 border border-amber-400/20 rounded-full px-3 py-1 animate-pulse">
                Click a target node to connect…
              </span>
            )}
            <button
              onClick={handleSave}
              className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold transition-all ${saved ? 'bg-emerald-600 text-white' : 'bg-violet-600 hover:bg-violet-500 text-white'}`}
            >
              <Save className="w-4 h-4" />
              {saved ? 'Saved ✓' : 'Save Workflow'}
            </button>
          </div>
        </div>

        <div className="flex-1 flex overflow-hidden">

          {/* ── Left palette ──────────────────────────────────────────────── */}
          <aside className="w-56 flex-shrink-0 bg-[#0a0c14]/90 backdrop-blur-sm border-r border-white/[0.04] overflow-y-auto p-3 space-y-4">
            <div>
              <p className="text-[9px] font-bold uppercase tracking-widest text-slate-600 px-1 mb-2">Triggers</p>
              <div className="space-y-1.5">
                {TRIGGER_TYPES.map(t => <PaletteBtn key={t} type={t} onAdd={addNode} />)}
              </div>
            </div>
            <div>
              <p className="text-[9px] font-bold uppercase tracking-widest text-slate-600 px-1 mb-2">AI Actions</p>
              <div className="space-y-1.5">
                {ACTION_TYPES.map(t => <PaletteBtn key={t} type={t} onAdd={addNode} />)}
              </div>
            </div>
            <div>
              <p className="text-[9px] font-bold uppercase tracking-widest text-slate-600 px-1 mb-2">Destinations</p>
              <div className="space-y-1.5">
                {DESTINATION_TYPES.map(t => <PaletteBtn key={t} type={t} onAdd={addNode} />)}
              </div>
            </div>

            {/* Legend */}
            <div className="rounded-xl bg-[#12121e]/60 border border-white/[0.06] p-3 space-y-1.5">
              <p className="text-[9px] font-bold uppercase tracking-widest text-slate-600 mb-2">Legend</p>
              {[
                { color: 'bg-cyan-500', label: 'Trigger node' },
                { color: 'bg-violet-500', label: 'AI Action node' },
                { color: 'bg-emerald-500', label: 'Destination node' },
              ].map(({ color, label }) => (
                <div key={label} className="flex items-center gap-2">
                  <div className={`w-2.5 h-2.5 rounded-full ${color}`} />
                  <span className="text-[11px] text-slate-400">{label}</span>
                </div>
              ))}
              <div className="pt-1 border-t border-slate-700/40">
                <div className="flex items-center gap-2">
                  <Info className="w-3 h-3 text-slate-600" />
                  <span className="text-[10px] text-slate-600">Click a node to select. Click again to edit.</span>
                </div>
              </div>
            </div>
          </aside>

          {/* ── Canvas ───────────────────────────────────────────────────── */}
          <div
            ref={canvasRef}
            className="flex-1 relative overflow-hidden cursor-default"
            style={{
              backgroundColor: '#07070f',
              backgroundImage: 'radial-gradient(circle, rgba(99,88,180,0.16) 1.5px, transparent 1.5px)',
              backgroundSize: '28px 28px'
            }}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onClick={() => { setSelected(null); if (connecting) setConnecting(null); }}
          >
            {/* SVG layer for edges */}
            <svg className="absolute inset-0 w-full h-full pointer-events-none" style={{ zIndex: 1 }}>
              {arrows.map(a => a && <Arrow key={a.key} x1={a.x1} y1={a.y1} x2={a.x2} y2={a.y2} />)}
            </svg>

            {/* Node cards */}
            {nodes.map(node => (
              <div
                key={node.id}
                onMouseDown={e => handleMouseDown(e, node.id)}
                onClick={e => {
                  e.stopPropagation();
                  if (connecting) { handleConnect(node.id); return; }
                  if (selected === node.id) { setDrawer(node); setSelected(null); }
                  else setSelected(node.id);
                }}
                style={{ position: 'absolute', left: node.x, top: node.y, zIndex: selected === node.id ? 10 : 2, width: 160 }}
              >
                <NodeCard
                  node={node}
                  selected={selected === node.id}
                  onClick={() => {}}
                  onDelete={() => deleteNode(node.id)}
                />
              </div>
            ))}

            {/* Empty state */}
            {nodes.length === 0 && (
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="text-center space-y-2">
                  <Layers className="w-12 h-12 text-slate-700 mx-auto" />
                  <p className="text-slate-500 text-sm font-medium">Canvas is empty</p>
                  <p className="text-slate-600 text-xs">Add nodes from the left panel to build your workflow</p>
                </div>
              </div>
            )}

            {/* Connect mode toolbar */}
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex items-center gap-2 bg-[#0f0f1a]/90 border border-white/[0.08] rounded-2xl px-4 py-2 backdrop-blur shadow-xl">
              <button
                onClick={e => { e.stopPropagation(); setConnecting(selected ?? null); }}
                disabled={!selected}
                className="flex items-center gap-1.5 text-xs font-medium text-slate-400 hover:text-violet-400 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronRight className="w-3.5 h-3.5" /> Connect selected
              </button>
              <div className="w-px h-4 bg-slate-700" />
              <button
                onClick={e => { e.stopPropagation(); if (selected) setDrawer(nodes.find(n => n.id === selected) ?? null); }}
                disabled={!selected}
                className="flex items-center gap-1.5 text-xs font-medium text-slate-400 hover:text-violet-400 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                <Cpu className="w-3.5 h-3.5" /> Edit prompt
              </button>
              <div className="w-px h-4 bg-slate-700" />
              <button
                onClick={e => { e.stopPropagation(); if (selected) deleteNode(selected); }}
                disabled={!selected}
                className="flex items-center gap-1.5 text-xs font-medium text-slate-400 hover:text-rose-400 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                <Trash2 className="w-3.5 h-3.5" /> Delete
              </button>
              <div className="w-px h-4 bg-slate-700" />
              <button
                onClick={e => { e.stopPropagation(); }}
                className="flex items-center gap-1.5 text-xs font-medium text-slate-400 hover:text-emerald-400 transition-colors"
              >
                <Play className="w-3.5 h-3.5" /> Run workflow
              </button>
            </div>
          </div>

          {/* ── Right properties panel (when node selected) ──────────────── */}
          {selectedNode && (
            <aside className="w-64 flex-shrink-0 bg-[#0a0c14] border-l border-slate-800/60 p-4 space-y-4 overflow-y-auto">
              <div>
                <p className="text-[9px] font-bold uppercase tracking-widest text-slate-600 mb-3">Node Properties</p>
                <div className={`rounded-xl bg-gradient-to-br ${NODE_META[selectedNode.type].color} p-0.5`}>
                  <div className="rounded-[11px] bg-slate-900 p-3">
                    <p className="font-semibold text-white text-sm">{selectedNode.label}</p>
                    <p className="text-[11px] text-slate-500 mt-0.5 capitalize">{selectedNode.kind} · {selectedNode.type}</p>
                  </div>
                </div>
              </div>
              <div>
                <p className="text-[9px] font-bold uppercase tracking-widest text-slate-600 mb-2">Description</p>
                <p className="text-xs text-slate-400 leading-relaxed">{NODE_META[selectedNode.type].desc}</p>
              </div>
              {selectedNode.kind === 'action' && (
                <div>
                  <p className="text-[9px] font-bold uppercase tracking-widest text-slate-600 mb-2">System Prompt</p>
                  <p className="text-[11px] text-slate-500 leading-relaxed bg-slate-800/60 rounded-lg p-2 border border-slate-700/40 font-mono">
                    {selectedNode.systemPromptOverride ?? DEFAULT_PROMPTS[selectedNode.type] ?? 'No prompt set'}
                  </p>
                </div>
              )}
              <button
                onClick={() => { setDrawer(selectedNode); setSelected(null); }}
                className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-violet-600 hover:bg-violet-500 rounded-xl text-sm text-white font-semibold transition-colors"
              >
                <Cpu className="w-4 h-4" /> Open Node Editor
              </button>
            </aside>
          )}
        </div>
      </div>

      {/* Node Drawer */}
      {drawerNode && (
        <NodeDrawer
          node={drawerNode}
          onClose={() => setDrawer(null)}
          onSave={saveNode}
        />
      )}
    </Layout>
  );
};

export default CanvasDesigner;
